import { Search, X } from "lucide-react";
import type { ListParams } from "@/hooks/use-time-entries";

export interface HistoryFilterState {
  dateFrom: string;
  dateTo: string;
  woNumber: string;
  type: "" | "billable" | "overhead";
  status: "" | "draft" | "submitted" | "approved" | "flagged";
  q: string;
}

export function defaultFilters(): HistoryFilterState {
  const now = new Date();
  const eightWeeksAgo = new Date(now);
  eightWeeksAgo.setDate(now.getDate() - 56);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return {
    dateFrom: fmt(eightWeeksAgo),
    dateTo: fmt(now),
    woNumber: "",
    type: "",
    status: "",
    q: "",
  };
}

export function filtersToListParams(f: HistoryFilterState): ListParams {
  return {
    dateFrom: f.dateFrom || undefined,
    dateTo: f.dateTo || undefined,
    woNumber: f.woNumber || undefined,
    type: f.type || undefined,
    status: f.status || undefined,
    q: f.q || undefined,
    limit: 200,
  };
}

interface HistoryFiltersProps {
  value: HistoryFilterState;
  onChange: (next: HistoryFilterState) => void;
}

export function HistoryFilters({ value, onChange }: HistoryFiltersProps) {
  const isCustom = JSON.stringify(value) !== JSON.stringify(defaultFilters());

  function update<K extends keyof HistoryFilterState>(key: K, v: HistoryFilterState[K]) {
    onChange({ ...value, [key]: v });
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card__body" style={{ display: "grid", gap: 12 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
          }}
        >
          <div className="form-field">
            <label className="form-field__label" htmlFor="filter-date-from">
              From
            </label>
            <input
              id="filter-date-from"
              type="date"
              className="form-input"
              value={value.dateFrom}
              max={value.dateTo || undefined}
              onChange={(e) => update("dateFrom", e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="filter-date-to">
              To
            </label>
            <input
              id="filter-date-to"
              type="date"
              className="form-input"
              value={value.dateTo}
              min={value.dateFrom || undefined}
              onChange={(e) => update("dateTo", e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="filter-wo">
              WO Number
            </label>
            <input
              id="filter-wo"
              type="text"
              className="form-input"
              placeholder="e.g. WO-1234"
              value={value.woNumber}
              onChange={(e) => update("woNumber", e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="filter-type">
              Type
            </label>
            <select
              id="filter-type"
              className="form-input"
              value={value.type}
              onChange={(e) => update("type", e.target.value as HistoryFilterState["type"])}
            >
              <option value="">All types</option>
              <option value="billable">Billable</option>
              <option value="overhead">Overhead</option>
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="filter-status">
              Status
            </label>
            <select
              id="filter-status"
              className="form-input"
              value={value.status}
              onChange={(e) => update("status", e.target.value as HistoryFilterState["status"])}
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="flagged">Flagged</option>
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="filter-q">
              Search notes
            </label>
            <div style={{ position: "relative" }}>
              <Search
                size={14}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--muted)",
                }}
              />
              <input
                id="filter-q"
                type="text"
                className="form-input"
                style={{ paddingLeft: 30 }}
                placeholder="Search…"
                value={value.q}
                onChange={(e) => update("q", e.target.value)}
              />
            </div>
          </div>
        </div>
        {isCustom && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onChange(defaultFilters())}
            >
              <X size={14} /> Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
